import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearRetreatData } from '../../../redux/slices/retreatSlice';
import retreatService from '../services/retreatService';
import './RetreatPayment.css';

const API_BASE_URL = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const RetreatPayment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const campIdParam = searchParams.get('campId');

  const { user } = useSelector((state) => state.auth);
  const { paymentInfo, selectedRetreat, campDates, lastRegInfo } = useSelector((state) => state.retreat);

  const campId     = paymentInfo?.campId     || parseInt(campIdParam) || 0;
  const userId     = paymentInfo?.userId     || user?.User_Id || user?.user_Id || 0;
  const totalAmount     = paymentInfo?.totalAmount     || 0;
  const retreatName     = paymentInfo?.campName        || selectedRetreat?.camp_Name || selectedRetreat?.Camp_Name || '';
  const registrationId  = paymentInfo?.registrationId  || lastRegInfo?.registrationId || 0;

  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim(),
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    bankName: '',
    zipCode: String(user?.User_Zipcode || user?.user_Zipcode || ''),
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [lookingUpBank, setLookingUpBank] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);

  useEffect(() => {
    if (!paymentInfo && !lastRegInfo) {
      navigate(`/retreat/registration?campId=${campIdParam || ''}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    const loadScript = () =>
      new Promise((resolve) => {
        if (window.grecaptcha) return resolve();
        const existing = document.querySelector('script[src*="recaptcha"]');
        if (existing) { existing.addEventListener('load', resolve); return; }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true; s.defer = true; s.onload = resolve;
        document.body.appendChild(s);
      });

    const renderWidget = () => {
      const el = document.getElementById('retreatpay-recaptcha');
      if (!el || recaptchaWidgetId !== null) return;
      const poll = () => {
        const api = window.grecaptcha;
        if (!api) { setTimeout(poll, 150); return; }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') { setTimeout(doRender, 150); return; }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (tok) => setRecaptchaToken(tok),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback':   () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) { setTimeout(doRender, 200); }
        };
        typeof api.ready === 'function' ? api.ready(doRender) : setTimeout(doRender, 0);
      };
      poll();
    };

    let active = true;
    loadScript().then(() => { if (active) setTimeout(renderWidget, 0); });
    return () => { active = false; };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  const resetRecaptcha = () => {
    try {
      if (recaptchaWidgetId !== null && window.grecaptcha) {
        window.grecaptcha.reset(recaptchaWidgetId);
        setRecaptchaToken('');
      }
    } catch (e) { console.warn('reCAPTCHA reset error:', e); }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (name === 'cardNumber') { const d = val.replace(/\D/g, ''); val = d.replace(/(\d{4})(?=\d)/g, '$1 ').trim(); }
    if (name === 'expiryDate') { const d = val.replace(/\D/g, ''); val = d.length <= 2 ? d : d.slice(0, 2) + '/' + d.slice(2, 4); }
    if (name === 'cvv') val = val.replace(/\D/g, '');
    if (name === 'routingNumber') val = val.replace(/\D/g, '').slice(0, 9);
    if (name === 'accountNumber') val = val.replace(/\D/g, '');
    if (name === 'zipCode') val = val.replace(/[^\d-]/g, '').slice(0, 10);
    setFormData((p) => ({ ...p, [name]: val }));
    if (validationErrors[name]) setValidationErrors((p) => { const n = { ...p }; delete n[name]; return n; });
    if (name === 'routingNumber' && val.length === 9) lookupBankName(val);
  };

  const handleMethodChange = (method) => { setPaymentMethod(method); setValidationErrors({}); };

  const lookupBankName = async (routingNumber) => {
    setLookingUpBank(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/CommonChannel/GetBankNameByRoutingNumber?routingNumber=${routingNumber}`, { headers: { Accept: 'application/json' } });
      if (resp.ok) { const data = await resp.json(); const name = data.bankName || data.name || data.institutionName || ''; if (name && name !== 'string') setFormData((p) => ({ ...p, bankName: name.trim() })); }
    } catch (e) { console.warn('Bank lookup error:', e); } finally { setLookingUpBank(false); }
  };

  const validateCC = () => {
    const errs = {};
    const digits = formData.cardNumber.replace(/\D/g, '');
    if (!digits) errs.cardNumber = 'Card number is required';
    else if (digits.length < 13 || digits.length > 19) errs.cardNumber = 'Card number must be 13–19 digits';
    if (!formData.expiryDate) { errs.expiryDate = 'Expiry date is required'; }
    else { const m = formData.expiryDate.match(/^(\d{2})\/(\d{2})$/); if (!m) { errs.expiryDate = 'Use MM/YY format'; } else { const mo = parseInt(m[1], 10); const yr = parseInt(m[2], 10); const now = new Date(); const cy = now.getFullYear() % 100; const cm = now.getMonth() + 1; if (mo < 1 || mo > 12) errs.expiryDate = 'Invalid month'; else if (yr < cy || (yr === cy && mo < cm)) errs.expiryDate = 'Card has expired'; } }
    if (!formData.cvv) errs.cvv = 'CVV is required'; else if (formData.cvv.length < 3) errs.cvv = 'CVV must be 3–4 digits';
    const zipRe = /^\d{5}(-\d{4})?$/;
    if (!formData.zipCode.trim()) errs.zipCode = 'ZIP code is required'; else if (!zipRe.test(formData.zipCode.trim())) errs.zipCode = 'Enter a valid US ZIP code';
    return errs;
  };

  const validateACH = () => {
    const errs = {};
    if (!formData.accountHolderName?.trim()) errs.accountHolderName = 'Account holder name is required';
    if (!formData.routingNumber) errs.routingNumber = 'Routing number is required'; else if (formData.routingNumber.length !== 9) errs.routingNumber = 'Routing number must be 9 digits';
    if (!formData.accountNumber) errs.accountNumber = 'Account number is required'; else if (formData.accountNumber.length < 5 || formData.accountNumber.length > 17) errs.accountNumber = 'Account number must be 5–17 digits';
    if (!formData.bankName?.trim()) errs.bankName = 'Bank name is required';
    const zipRe = /^\d{5}(-\d{4})?$/;
    if (!formData.zipCode.trim()) errs.zipCode = 'ZIP code is required'; else if (!zipRe.test(formData.zipCode.trim())) errs.zipCode = 'Enter a valid US ZIP code';
    return errs;
  };

  const fetchBillingCodes = async () => {
    const stateId   = user?.State_Id   || user?.state_Id   || user?.User_State   || user?.user_State || '';
    const rawCountry= user?.Country_Id || user?.country_Id || user?.User_Country  || user?.user_Country || '1';
    const countryId = parseInt(rawCountry) ? String(parseInt(rawCountry)) : '1';
    let stateCode   = user?.StateSortCode   || user?.stateSortCode   || '';
    let countryCode = user?.CountrySortCode || user?.countrySortCode || '';
    if (!stateCode && stateId && countryId) {
      try { const r = await fetch(`${API_BASE_URL}/CommonChannel/GetStatesByCountry?countryId=${countryId}`); if (r.ok) { const states = await r.json(); const sid = parseInt(stateId); const found = states.find((s) => s.UserStateId === sid || s.userStateId === sid || s.state_Id === sid); stateCode = found?.ShortCode || found?.shortCode || found?.sortCode || found?.SortCode || ''; } } catch (e) { console.warn('State lookup:', e); }
    }
    if (!countryCode && countryId) {
      try { const r = await fetch(`${API_BASE_URL}/CommonChannel/GetCountries`); if (r.ok) { const countries = await r.json(); const cid = parseInt(countryId); const found = countries.find((c) => c.country_Id === cid || c.countryId === cid); countryCode = found?.sortCode || found?.SortCode || found?.country_short || ''; } } catch (e) { console.warn('Country lookup:', e); }
    }
    return { stateCode: stateCode || 'GA', countryCode: countryCode || 'US' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setValidationErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
      document.getElementById('retreatpay-recaptcha')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const errs = paymentMethod === 'credit' ? validateCC() : validateACH();
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs);
      document.getElementsByName(Object.keys(errs)[0])[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setProcessing(true);
    try {
      const { stateCode, countryCode } = await fetchBillingCodes();
      const userName    = `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim();
      const userEmail   = user?.User_Email || user?.user_Email || user?.email || '';
      const userPhone   = user?.User_Phone || user?.User_Cell_Phone_No || user?.user_Phone || '';
      const timestamp   = new Date().toLocaleString();
      const retreatDateRange = campDates ? `${formatDate(campDates.campStartDate || campDates.CampStartDate)} – ${formatDate(campDates.campEndDate || campDates.CampEndDate)}` : '';
      const billingAddress   = [user?.User_Address_Line1 || user?.user_Address_Line1 || '', user?.User_City || user?.user_City || '', stateCode, formData.zipCode.trim()].filter(Boolean).join(', ');

      const allStudentEntries = paymentInfo?.students || [];
      const studentName = paymentInfo?.studentName || '';
      const buildStudentLine = (entry) => {
        const name = entry.studentName || '';
        const wLabel = (entry.selectedWeeksData || []).map(w => `Session ${w.weekNumber} (${w.dateRange})`).join(', ');
        const extNums = entry.extendedWeekNumbers || [];
        const extLabel = extNums.length > 0 ? `Accommodation: ${extNums.map(wn => { const wd = (entry.selectedWeeksData || []).find(w => w.weekNumber === wn); return wd ? `Session ${wn} (${wd.dateRange})` : `Session ${wn}`; }).join(', ')}` : '';
        return [name, wLabel, extLabel].filter(Boolean).join(' | ');
      };
      const scheduleHtml = allStudentEntries.length > 1
        ? allStudentEntries.map(buildStudentLine).join('\n')
        : (() => {
            const weeksLabel = paymentInfo?.weeksLabel || '';
            const extNums = paymentInfo?.extendedWeekNumbers || [];
            const selWeeksData = paymentInfo?.selectedWeeksData || [];
            const extLabel = extNums.length > 0 ? `Accommodation: ${extNums.map(wn => { const wd = selWeeksData.find(w => w.weekNumber === wn); return wd ? `Session ${wn} (${wd.dateRange})` : `Session ${wn}`; }).join(', ')}` : '';
            return [studentName, weeksLabel, extLabel].filter(Boolean).join(' | ');
          })();

      const basePayload = {
        amount: totalAmount, campId, campName: retreatName, userId, userEmail, userName, userPhone,
        streetAddress1: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
        streetAddress2: user?.User_Address_Line2 || user?.user_Address_Line2 || '',
        city: user?.User_City || user?.user_City || '',
        state: stateCode, stateCode, zipCode: formData.zipCode.trim(), postalCode: formData.zipCode.trim(),
        country: countryCode, paymentMethod, transactionDate: new Date().toISOString(),
      };

      const paymentPayload = paymentMethod === 'credit'
        ? { ...basePayload, paymentType: 'CC', cardNumber: formData.cardNumber.replace(/\s/g, ''), expiryDate: formData.expiryDate, cvv: formData.cvv, cardholderName: formData.cardholderName }
        : { ...basePayload, paymentType: 'ACH', accountHolderName: formData.accountHolderName, routingNumber: formData.routingNumber, accountNumber: formData.accountNumber, accountType: formData.accountType, bankName: formData.bankName || 'Financial Institution' };

      const stepStatus = {
        'Create Order': { success: false, message: 'Pending…' },
        'Payment':      { success: false, message: 'Pending…' },
        'Confirmation': { success: false, message: 'Pending…' },
        'Email':        { success: false, message: 'Pending…' },
      };

      let campOrderId = 0, campPaymentId = 0;
      try {
        const or = await retreatService.createOrder({ userId, campId, totalAmount, feeAmount: totalAmount, discountAmount: 0, donationAmount: 0, accommodationAmount: 0, otherDonation: 'NA', paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH' });
        campOrderId   = or?.Camp_Order_ID || or?.camp_Order_ID || or?.camp_Order_Id || 0;
        campPaymentId = or?.Camp_Payment_Id || or?.camp_Payment_Id || 0;
        stepStatus['Create Order'] = { success: true, message: `Order #${campOrderId} created` };
      } catch (orderErr) { stepStatus['Create Order'] = { success: false, message: 'Order record could not be created — payment will still proceed', error: orderErr?.message }; }

      let paymentResponse, transId = '';
      try {
        paymentResponse = await retreatService.processPayment(paymentPayload);
        const isApproved =
          paymentResponse?.createTransactionResponse?.transactionResponse?.responseCode === '1' ||
          paymentResponse?.createTransactionResponse?.messages?.resultCode === 'Ok';
        if (!isApproved) {
          const msg = paymentResponse?.meta?.errors?.errorMessages?.[0]?.message || paymentResponse?.message || paymentResponse?.createTransactionResponse?.transactionResponse?.messages?.[0]?.description || 'Payment was not approved by gateway';
          throw new Error(msg);
        }
        transId = paymentResponse?.createTransactionResponse?.transactionResponse?.transId || '';
        stepStatus['Payment'] = { success: true, message: `Approved · Trans ID: ${transId}` };
      } catch (payErr) {
        stepStatus['Payment'] = { success: false, message: payErr.message || 'Payment gateway declined or did not respond', error: payErr?.message };
        retreatService.sendAdminAlertEmail({ stepStatus, transactionId: transId, retreatName, campId, userId, userName, userEmail, totalAmount, paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH', timestamp, errorDetails: payErr?.message }).catch(() => {});
        throw payErr;
      }

      try {
        await retreatService.updatePaymentStatus({ Camp_Id: campId, User_Id: userId, Camp_Payment_Id: campPaymentId || registrationId, Camp_Order_Id: campOrderId, Receipt_Id: transId || '0' });
        stepStatus['Confirmation'] = { success: true, message: 'Registration confirmed in database' };
      } catch (confErr) { stepStatus['Confirmation'] = { success: false, message: `Payment charged (Trans: ${transId}) but DB confirmation failed — contact support`, error: confErr?.message }; }

      let receiptUrl = '';
      try {
        const emailResp = await retreatService.sendConfirmationEmail({
          toEmail: userEmail, userName, campName: retreatName, userPhone, registrationId,
          amount: String(totalAmount), paymentType: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          transactionId: transId, retreatDateRange, studentName,
          students: allStudentEntries, scheduleHtml: scheduleHtml || studentName || retreatName, billingAddress,
          userFirstName: paymentInfo?.userFirstName || user?.User_First_Name || user?.user_First_Name || '',
          userLastName:  paymentInfo?.userLastName  || user?.User_Last_Name  || user?.user_Last_Name  || '',
        });
        receiptUrl = (typeof emailResp === 'string' ? emailResp : '') || emailResp?.receiptUrl || emailResp?.ReceiptUrl || emailResp?.attachmentUrl || emailResp?.data?.receiptUrl || emailResp?.data?.attachmentUrl || '';
        stepStatus['Email'] = { success: true, message: `Confirmation sent to ${userEmail}` };
      } catch (emailErr) { stepStatus['Email'] = { success: false, message: 'Confirmation email could not be sent — retreat staff will follow up', error: emailErr?.message }; }

      const anyFailure = Object.values(stepStatus).some((s) => !s.success);
      retreatService.sendAdminAlertEmail({
        stepStatus, transactionId: transId, retreatName, campId, userId, userName, userEmail, totalAmount,
        paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH', timestamp,
        errorDetails: anyFailure ? Object.entries(stepStatus).filter(([, v]) => !v.success).map(([k, v]) => `${k}: ${v.error || v.message}`).join('\n') : null,
      }).catch(() => {});

      setPaymentResult({ transactionId: transId, retreatName, totalAmount, paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH Bank Account', transactionDate: timestamp, retreatDateRange, receiptUrl, stepStatus, hasPartialFailure: anyFailure });
      setShowSuccessModal(true);
      resetRecaptcha();
    } catch (err) {
      console.error('Payment error:', err);
      const msg = err.response?.data?.meta?.errors?.errorMessages?.[0]?.message || err.response?.data?.message || err.message || 'Payment failed. Please try again.';
      setErrorMessage(msg);
      setShowErrorModal(true);
      resetRecaptcha();
    } finally { setProcessing(false); }
  };

  const handleDone = () => {
    dispatch(clearRetreatData());
    setShowSuccessModal(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-4 lg:mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-3xl font-extrabold text-gray-900">Payment Details</h1>
          <p className="text-gray-500 text-xs lg:text-sm mt-0.5">Complete your retreat registration payment</p>
        </div>
        <button onClick={() => navigate(-1)} className="flex-shrink-0 flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold px-4 py-2 lg:px-5 lg:py-2.5 rounded-full shadow-md transition text-sm">← Back</button>
      </div>

      <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-4 lg:gap-6 items-start">
        {/* Order Summary */}
        <div className="w-full lg:w-2/5 bg-white rounded-2xl shadow-xl p-4 lg:p-6 order-first lg:sticky lg:top-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Enrollment Summary</h2>
          {(() => {
            const students = paymentInfo?.students;
            const isMulti = students && students.length > 1;
            const renderCard = (s, idx) => {
              const weeksData = s.selectedWeeksData || [];
              const weekFee = s.weekFee || 0;
              const extFee  = s.extFee  || 0;
              const extWeekNums = s.extendedWeekNumbers || [];
              const subtotal   = weeksData.reduce((sum, w) => sum + (w.fee || weekFee), 0);
              const discountAmt = weeksData.length > 1 ? subtotal * 0.10 : 0;
              const extTotal   = weeksData.filter(w => w.hasExtended).reduce((sum, w) => sum + (w.extFee || extFee), 0) || extWeekNums.length * extFee;
              const gLow = (s.studentGender || '').toLowerCase();
              return (
                <div key={s.studentId || idx} className={isMulti ? 'border border-gray-200 rounded-2xl p-4 mb-4' : ''}>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-xl flex-shrink-0">{gLow === 'male' || gLow === 'm' ? '👦' : gLow === 'female' || gLow === 'f' ? '👧' : '👤'}</div>
                    <div className="min-w-0"><p className="font-bold text-gray-900 text-sm truncate">{s.studentName}</p><p className="text-gray-500 text-xs">{weeksData.length} session{weeksData.length !== 1 ? 's' : ''} selected</p></div>
                  </div>
                  {weeksData.length > 0 && (
                    <div className="space-y-1.5 mb-3">
                      {weeksData.map((w) => (
                        <div key={w.weekNumber} className="rounded-lg p-2.5 border bg-white border-gray-200 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-md bg-gray-700 text-white flex items-center justify-center font-bold text-xs shadow flex-shrink-0">{w.weekNumber}</div>
                            <div className="min-w-0"><p className="font-semibold text-gray-800 text-xs truncate">{w.label}</p><p className="text-gray-400 text-xs truncate">{w.dateRange}</p></div>
                          </div>
                          <div className="text-right"><p className="font-bold text-gray-800 text-xs">${w.fee}</p>{w.hasExtended && <p className="text-orange-500 text-xs">+${extFee} stay</p>}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 text-xs space-y-1">
                    {weeksData.length > 1 && <div className="flex justify-between"><span className="text-gray-500">{weeksData.length} sessions × ${weekFee}</span><span className="font-semibold">${subtotal.toFixed(2)}</span></div>}
                    {discountAmt > 0 && <div className="flex justify-between"><span className="text-green-600">10% multi-session discount</span><span className="font-semibold text-green-600">−${discountAmt.toFixed(2)}</span></div>}
                    {extWeekNums.length > 0 && <div className="flex justify-between"><span className="text-orange-500">Accommodation × {extWeekNums.length}</span><span className="font-semibold text-orange-500">+${extTotal.toFixed(2)}</span></div>}
                    <div className="border-t border-gray-200 pt-1 flex justify-between font-bold"><span>Subtotal</span><span>${s.grandTotal?.toFixed(2) || '0.00'}</span></div>
                  </div>
                </div>
              );
            };
            return (
              <>
                {isMulti ? <>{students.map((s, i) => renderCard(s, i))}</> : renderCard(students?.[0] || { studentName: paymentInfo?.studentName, studentGender: paymentInfo?.studentGender, selectedWeeksData: paymentInfo?.selectedWeeksData || [], extendedWeekNumbers: paymentInfo?.extendedWeekNumbers || [], weekFee: paymentInfo?.weekFee || 0, extFee: paymentInfo?.extFee || 0, grandTotal: paymentInfo?.totalAmount || 0 }, 0)}
                <div className="border-t-2 border-gray-200 pt-4 mt-2 flex justify-between items-center">
                  <span className="font-black text-gray-900 text-base">Total Amount</span>
                  <span className="font-black text-2xl text-gray-900">${parseFloat(totalAmount).toFixed(2)}</span>
                </div>
              </>
            );
          })()}
        </div>

        {/* Payment Form */}
        <div className="flex-1 w-full bg-white rounded-2xl shadow-xl p-4 lg:p-6">
          <p className="text-base font-bold text-gray-900 mb-4">Select Payment Method</p>
          <div className="flex rounded-xl overflow-hidden border border-gray-200 mb-6">
            <button type="button" onClick={() => handleMethodChange('credit')} className={`flex-1 py-2.5 font-semibold text-sm transition ${paymentMethod === 'credit' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>💳 Credit/Debit Card</button>
            <button type="button" onClick={() => handleMethodChange('ach')} className={`flex-1 py-2.5 font-semibold text-sm transition border-l border-gray-200 ${paymentMethod === 'ach' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>🏦 Bank Account (ACH)</button>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {paymentMethod === 'credit' ? (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-4">Card Information</p>
                <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-1">Card Number *</label><input name="cardNumber" value={formData.cardNumber} onChange={handleInputChange} placeholder="1234 5678 9012 3456" maxLength="19" autoComplete="cc-number" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.cardNumber ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{validationErrors.cardNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.cardNumber}</p>}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label><input name="expiryDate" value={formData.expiryDate} onChange={handleInputChange} placeholder="MM/YY" maxLength="5" autoComplete="cc-exp" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.expiryDate ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{validationErrors.expiryDate && <p className="text-red-500 text-xs mt-1">{validationErrors.expiryDate}</p>}</div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">CVV *</label><input name="cvv" type="password" value={formData.cvv} onChange={handleInputChange} placeholder="123" maxLength="4" autoComplete="cc-csc" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.cvv ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{validationErrors.cvv && <p className="text-red-500 text-xs mt-1">{validationErrors.cvv}</p>}</div>
                </div>
                <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name *</label><input name="cardholderName" value={formData.cardholderName} onChange={handleInputChange} placeholder="Name on card" autoComplete="cc-name" className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition" /><p className="text-gray-400 text-xs mt-1">✏️ Edit if card belongs to a different name</p></div>
                <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-1">Billing ZIP Code *</label><input name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="e.g. 30096" maxLength="10" autoComplete="postal-code" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.zipCode ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{validationErrors.zipCode && <p className="text-red-500 text-xs mt-1">{validationErrors.zipCode}</p>}</div>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-4">Bank Account Information</p>
                <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name *</label><input name="accountHolderName" value={formData.accountHolderName} onChange={handleInputChange} placeholder="Full name as on bank account" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.accountHolderName ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{validationErrors.accountHolderName && <p className="text-red-500 text-xs mt-1">{validationErrors.accountHolderName}</p>}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Routing Number *</label><div className="relative"><input name="routingNumber" value={formData.routingNumber} onChange={handleInputChange} placeholder="9-digit routing" maxLength="9" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.routingNumber ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{lookingUpBank && <div className="absolute right-3 top-3 w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />}</div>{validationErrors.routingNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.routingNumber}</p>}</div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label><input name="accountNumber" value={formData.accountNumber} onChange={handleInputChange} placeholder="Account number" maxLength="17" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.accountNumber ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{validationErrors.accountNumber && <p className="text-red-500 text-xs mt-1">{validationErrors.accountNumber}</p>}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Account Type *</label><select name="accountType" value={formData.accountType} onChange={handleInputChange} className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition"><option value="checking">Checking</option><option value="savings">Savings</option></select></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label><input name="bankName" value={formData.bankName} onChange={handleInputChange} placeholder="Auto-filled or enter manually" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.bankName ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{validationErrors.bankName && <p className="text-red-500 text-xs mt-1">{validationErrors.bankName}</p>}</div>
                </div>
                <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-1">Billing ZIP Code *</label><input name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="e.g. 30096" maxLength="10" autoComplete="postal-code" className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-300 outline-none transition ${validationErrors.zipCode ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />{validationErrors.zipCode && <p className="text-red-500 text-xs mt-1">{validationErrors.zipCode}</p>}</div>
              </>
            )}

            {RECAPTCHA_SITE_KEY && (
              <div className="my-5">
                <div id="retreatpay-recaptcha" />
                {validationErrors.recaptcha && <p className="text-red-500 text-xs mt-1">{validationErrors.recaptcha}</p>}
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button type="button" onClick={() => navigate(-1)} className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button type="submit" disabled={processing} className="flex-1 bg-white hover:bg-gray-50 disabled:opacity-40 text-gray-800 font-bold py-3 rounded-xl border-2 border-gray-300 shadow-[0_4px_0_rgba(0,0,0,0.1),0_4px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_0_rgba(0,0,0,0.1)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all flex items-center justify-center gap-2">
                {processing ? (<><div className="w-4 h-4 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" /> Processing…</>) : (`Pay $${parseFloat(totalAmount).toFixed(2)}`)}
              </button>
            </div>
            <p className="text-center text-gray-400 text-xs mt-3">🔒 Secured by Authorize.Net · 256-bit SSL encryption</p>
          </form>
        </div>
      </div>

      {/* Processing Overlay */}
      {processing && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-md z-50 flex items-center justify-center">
          <div className="text-center select-none">
            <div className="relative inline-flex items-center justify-center mb-8">
              <span className="absolute inline-flex w-40 h-40 rounded-full bg-white/10 animate-ping" style={{ animationDuration: '1.4s' }} />
              <span className="absolute inline-flex w-32 h-32 rounded-full bg-white/15 animate-ping" style={{ animationDuration: '1.1s', animationDelay: '0.2s' }} />
              <div className="relative w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(255,255,255,0.25)] z-10">
                <img src="https://www.radhamadhavsociety.org/assets/images/about/i-logo-2.png" alt="RMS" className="w-16 h-16 object-contain" style={{ animation: 'retreatLogoPulse 2s ease-in-out infinite' }} />
              </div>
            </div>
            <h3 className="text-white text-2xl font-bold mb-1 tracking-tight">Processing Payment</h3>
            <p className="text-white/60 text-sm mb-6">Please wait · do not close this window</p>
            <div className="flex justify-center gap-2">{[0, 150, 300].map((delay) => (<div key={delay} className="w-2.5 h-2.5 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />))}</div>
          </div>
          <style>{`@keyframes retreatLogoPulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.08); opacity: 0.85; } }`}</style>
        </div>
      )}

      {/* Success / Partial-Success Modal */}
      {showSuccessModal && (() => {
        const partial = paymentResult?.hasPartialFailure;
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col" style={{ maxHeight: 'min(90dvh,90vh)' }}>
              <div className={`${partial ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-green-500 to-emerald-600'} p-6 text-center flex-shrink-0`}>
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg"><span className="text-2xl">{partial ? '⚠️' : '✅'}</span></div>
                <h2 className="text-xl font-extrabold text-white">{partial ? 'Payment Processed — Action May Be Needed' : 'Payment Successful!'}</h2>
                <p className="text-white/80 text-sm mt-1">{partial ? 'Your card was charged. Some steps had issues — retreat staff has been alerted.' : 'Your retreat registration is confirmed'}</p>
              </div>
              <div className="overflow-y-auto flex-1 p-5 space-y-4">
                <div className="bg-gray-50 rounded-2xl p-4 space-y-2.5">
                  {paymentResult?.transactionId && <div className="flex justify-between text-sm"><span className="text-gray-500">Transaction ID</span><span className="font-mono font-bold text-gray-800 bg-gray-200 px-2 py-0.5 rounded">{paymentResult.transactionId}</span></div>}
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Retreat</span><span className="font-semibold text-gray-800 text-right max-w-[55%]">{paymentResult?.retreatName}</span></div>
                  {paymentResult?.retreatDateRange && <div className="flex justify-between text-sm"><span className="text-gray-500">Dates</span><span className="font-semibold text-gray-800">{paymentResult.retreatDateRange}</span></div>}
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Payment</span><span className="font-semibold text-gray-800">{paymentResult?.paymentMethod}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Date</span><span className="font-semibold text-gray-800">{paymentResult?.transactionDate}</span></div>
                  <div className="flex justify-between text-sm border-t pt-2"><span className="font-bold text-gray-800">Amount Paid</span><span className="font-black text-xl text-gray-900">${parseFloat(paymentResult?.totalAmount || 0).toFixed(2)}</span></div>
                </div>
                {paymentResult?.receiptUrl && (
                  <div className="flex gap-3">
                    <a href={paymentResult.receiptUrl} target="_blank" rel="noopener noreferrer" className="flex-1 text-center py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl text-sm shadow transition hover:opacity-90">👁 View Receipt</a>
                    <a href={paymentResult.receiptUrl} download className="flex-1 text-center py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl text-sm shadow transition hover:opacity-90">⬇ Download</a>
                  </div>
                )}
                {partial && paymentResult?.stepStatus && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                    <p className="font-bold text-amber-800 text-sm">Step Details:</p>
                    {Object.entries(paymentResult.stepStatus).map(([k, v]) => (
                      <div key={k} className="flex items-start gap-2 text-xs">
                        <span className={v.success ? 'text-green-600' : 'text-red-500'}>{v.success ? '✓' : '✗'}</span>
                        <div><span className="font-semibold text-gray-700">{k}: </span><span className="text-gray-600">{v.message}</span></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-5 border-t border-gray-100 flex-shrink-0">
                <button onClick={handleDone} className="w-full bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white font-bold py-3 px-6 rounded-xl transition shadow-lg">Done</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><span className="text-3xl">❌</span></div>
            <h2 className="text-xl font-extrabold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <div className="flex gap-3">
              <button onClick={() => setShowErrorModal(false)} className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-xl transition">Try Again</button>
              <button onClick={() => navigate(-1)} className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-50 transition">Go Back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetreatPayment;
